export type Serializable<T> =
    T extends Function ? never : // Exclude functions
        T extends Array<infer U> ? Serializable<U>[] : // Handle arrays
            T extends object ? {
                [K in keyof T as T[K] extends Function ? never : K]: Serializable<T[K]>
            } : T; // Handle objects

const isPrimitive = (value) => {
    return value !== Object(value);
}

const isFunction = (value) => {
    return typeof value === 'function';
}

export const containsPrimitivesOnly = (obj) => {
    if (isPrimitive(obj)) {
        return true;
    }

    if (isFunction(obj)) {
        return false
    }

    if (Object.getPrototypeOf(obj)) {
        return false
    }

    for (const key in obj) {
        const value = obj[key];
        if (typeof value === 'object') {
            // Recursively check if it's a nested object
            if (!containsPrimitivesOnly(value)) {
                return false;
            }
        } else if (isPrimitive(value)) {
            continue; // It's a primitive, so we can skip it
        } else {
            return false; // Found a non-primitive value
        }
    }

    return true; // All values are primitives or nested objects with primitives
}

// currently support root level only
// {x: 1, y : 'a', z : ()=>{...}, inner : { x: 1 , y : 'a'} }
// {x: 1, y : 'a'}
//https://developer.mozilla.org/en-US/docs/Glossary/Falsy
export type SerializableSkeleton<T> = Partial<Record<keyof T, boolean | Partial<Record<string, boolean>>>> | boolean;

export const makeSerializable = <T>(
    obj: T,
    skeleton: SerializableSkeleton<T> = {},
    includeFalsyKeys: boolean = true, // TODO : use that arg
    seen: WeakMap<object, Serializable<any>> = new WeakMap()
): Serializable<T> => {

    if (isPrimitive(obj)) {
        return obj as Serializable<T>; // Directly return primitive values
    }

    if (Array.isArray(obj)) {
        if (seen.has(obj)) {
            return seen.get(obj)!; // Return the already processed object
        }

        // Handle array serialization
        const serializableArray: Serializable<any>[] = [];
        seen.set(obj, serializableArray); // Store the array in seen

        for (let index = 0; index < obj.length; index++) {
            const element = obj[index];
            serializableArray[index] = makeSerializable(element, skeleton, includeFalsyKeys, seen);
        }
        return serializableArray as Serializable<T>;
    }

    if (obj !== null && typeof obj === 'object') {
        // Check for cyclic references
        if (seen.has(obj)) {
            return seen.get(obj)!; // Return the already processed object
        }
        const serializableObject: Partial<Serializable<T>> = {};
        seen.set(obj, serializableObject); // Store the object in seen

        for (const key in obj) {
            // Check if the key is in the skeleton and if it's a valid key to serialize

            const keyInSkeleton = typeof skeleton == "boolean" ? skeleton : skeleton[key];

            if (keyInSkeleton) {
                if (keyInSkeleton === true) {
                    // If the skeleton indicates to serialize this key
                    if (includeFalsyKeys ? obj[key] !== undefined : obj[key]) {
                        // @ts-ignore
                        serializableObject[key] = makeSerializable(obj[key], true, includeFalsyKeys, seen);
                    }
                } else if (typeof keyInSkeleton === 'object') {
                    // If the skeleton indicates nested keys to serialize
                    // @ts-ignore
                    serializableObject[key] = makeSerializable(obj[key], keyInSkeleton, includeFalsyKeys, seen);
                }
            }
        }
        return serializableObject as Serializable<T>;
    }

    return {} as any; // Return an empty object for other cases
}
